import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Typography,
  Box,
} from "@mui/material";
import {
  getBatchSubjectList,
  getChapterList,
  getSubTopicList,
} from "utils/metadata-split/metaDataApiFetch";

const QUESTION_TYPE_OPTIONS = [
  "DESCRIPTIVE",
  "FILL_IN_BLANKS",
  "MULTI_SELECT",
  "SINGLE_SELECT",
  "UNKNOWN",
];

const NODE_TYPE_OPTIONS = ["MainQuestion", "GroupQuestion", "SubQuestion"];

type QuestionData = {
  type?: string;
  marks?: number | string;
  subject?: string;
  subjectName?: string;
  chapter?: string;
  chapterName?: string;
  topic?: string;
  topicName?: string;
  nodeType?: string;
  choiceCount?: number;
};

type Props = {
  index: number;
  data: QuestionData;
  onChange: (newData: QuestionData) => void;
  examPaper?: any;
  sectionSubject?: string;
  sectionSubjectName?: string;
};

function QuestionForm({
  index,
  data,
  onChange,
  examPaper,
  sectionSubject,
  sectionSubjectName,
}: Props) {
  const [subjects, setSubjects] = useState<any[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [topics, setTopics] = useState<any[]>([]);

  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);

  const handleChange = (updates: Partial<QuestionData>) => {
    onChange({ ...data, ...updates });
  };

  const availableQuestionTypes =
    examPaper?.questionTypes || QUESTION_TYPE_OPTIONS;

  const isMainQuestion = data.nodeType === "MainQuestion";

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setLoadingSubjects(true);
        const res: any = await getBatchSubjectList();
        if (res && Array.isArray(res[0])) {
          setSubjects(res[0]);
        }
      } catch (error) {
        console.error("Error fetching subjects", error);
      } finally {
        setLoadingSubjects(false);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (!data.subject || data.subject !== sectionSubject) {
      setChapters([]);
      return;
    }
    const fetchChapters = async () => {
      try {
        setLoadingChapters(true);
        const params: any = {
          batch_id: process.env.REACT_APP_GENERIC_BATCH_ID,
          subject_id: data.subject,
        };
        const res: any = await getChapterList(params);
        if (res && Array.isArray(res[0])) {
          setChapters(res[0]);
        }
      } catch (error) {
        console.error("Error fetching chapters", error);
      } finally {
        setLoadingChapters(false);
      }
    };
    fetchChapters();
  }, [data.subject, sectionSubject]);

  useEffect(() => {
    if (!data.chapter) {
      setTopics([]);
      return;
    }
    const fetchTopics = async () => {
      try {
        setLoadingTopics(true);
        const params: any = {
          batch_id: process.env.REACT_APP_GENERIC_BATCH_ID,
          subject_id: data.subject,
          chapter_id: data.chapter,
        };
        const res: any = await getSubTopicList(params);
        if (res && Array.isArray(res[0])) {
          setTopics(res[0]);
        }
      } catch (error) {
        console.error("Error fetching topics", error);
      } finally {
        setLoadingTopics(false);
      }
    };
    fetchTopics();
  }, [data.chapter]);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={4}>
        {/* Q No. */}
        <Grid item xs={12} sm={3}>
          <TextField
            label="Q No."
            size="small"
            fullWidth
            value={index + 1}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Question Type</InputLabel>
            <Select
              value={data.type ?? ""}
              onChange={(e) => handleChange({ type: e.target.value })}
              label="Question Type"
            >
              <MenuItem value="">Select Type</MenuItem>
              {availableQuestionTypes.map((opt: any) => (
                <MenuItem
                  key={typeof opt === "string" ? opt : opt.value}
                  value={typeof opt === "string" ? opt : opt.value}
                >
                  {typeof opt === "string" ? opt : opt.label || opt.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Marks */}
        <Grid item xs={12} sm={3}>
          <TextField
            label="Marks"
            type="number"
            size="small"
            fullWidth
            value={data.marks ?? ""}
            onChange={(e) =>
              handleChange({
                marks: e.target.value ? Number(e.target.value) : "",
              })
            }
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Choice Count"
            type="number"
            size="small"
            fullWidth
            value={isMainQuestion ? 1 : data.choiceCount ?? 1}
            onChange={(e) =>
              handleChange({
                choiceCount: e.target.value ? Number(e.target.value) : 1,
              })
            }
            disabled={isMainQuestion}
            sx={{
              backgroundColor: isMainQuestion ? "grey.100" : "inherit",
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            helperText={
              isMainQuestion ? "Auto-set to 1 for Main Questions" : ""
            }
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Node Type</InputLabel>
            <Select
              value={data.nodeType ?? "MainQuestion"}
              onChange={(e) =>
                handleChange({
                  nodeType: e.target.value,
                  ...(e.target.value === "MainQuestion" && { choiceCount: 1 }),
                })
              }
              label="Node Type"
              disabled={isMainQuestion}
              sx={{
                backgroundColor: isMainQuestion ? "grey.100" : "inherit",
                borderRadius: 2,
              }}
            >
              {NODE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isMainQuestion && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Main Questions are auto-selected
            </Typography>
          )}
        </Grid>
        {/* <Grid item xs={12} sm={4}>
          {isMainQuestion ? (
            <Box>
              <TextField
                label="Subject (Section Level)"
                size="small"
                fullWidth
                value={sectionSubjectName || "No subject selected"}
                disabled
                sx={{
                  backgroundColor: 'grey.100',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Subject is managed at section level
              </Typography>
            </Box>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Subject</InputLabel>
              <Select
                value={String(data.subject ?? "")}
                onChange={(e) => {
                  const selectedSubject = subjects.find(
                    (s) => s.subject_id === e.target.value
                  );
                  handleChange({
                    subject: String(e.target.value),
                    subjectName: selectedSubject?.subject_name || "",
                    chapter: "",
                    chapterName: "",
                    topic: "",
                    topicName: "",
                  });
                }}
                label="Subject"
              >
                <MenuItem value="">Select Subject</MenuItem>
                {loadingSubjects ? (
                  <MenuItem disabled>
                    <CircularProgress size={18} /> Loading...
                  </MenuItem>
                ) : (
                  subjects.map((sub: any) => (
                    <MenuItem key={sub.subject_id} value={sub.subject_id}>
                      {sub.subject_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
        </Grid> */}

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" disabled={!data.subject}>
            <InputLabel>Chapter</InputLabel>
            <Select
              value={String(data.chapter ?? "")}
              onChange={(e) => {
                const selectedChapter = chapters.find(
                  (c) => c.chapter_id === e.target.value
                );
                handleChange({
                  chapter: String(e.target.value),
                  chapterName: selectedChapter?.chapter_name || "",
                  topic: "",
                  topicName: "",
                });
              }}
              label="Chapter"
              sx={{
                borderRadius: 2,
              }}
            >
              <MenuItem value="">Select Chapter</MenuItem>
              {loadingChapters ? (
                <MenuItem disabled>
                  <CircularProgress size={18} /> Loading...
                </MenuItem>
              ) : (
                chapters.map((ch: any) => (
                  <MenuItem key={ch.chapter_id} value={ch.chapter_id}>
                    {ch.chapter_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {!data.subject && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Select subject first
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" disabled={!data.chapter}>
            <InputLabel>Topic</InputLabel>
            <Select
              value={String(data.topic ?? "")}
              onChange={(e) => {
                const selectedTopic = topics.find(
                  (t) => t.topic_id === e.target.value
                );
                handleChange({
                  topic: String(e.target.value),
                  topicName: selectedTopic?.topic_name || "",
                });
              }}
              label="Topic"
              sx={{
                borderRadius: 2,
              }}
            >
              <MenuItem value="">Select Topic</MenuItem>
              {loadingTopics ? (
                <MenuItem disabled>
                  <CircularProgress size={18} /> Loading...
                </MenuItem>
              ) : (
                topics.map((tp: any) => (
                  <MenuItem key={tp.topic_id} value={tp.topic_id}>
                    {tp.topic_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {!data.chapter && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Select chapter first
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default QuestionForm;
